import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps, IconThemableProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomFieldDefinition } from "@midland-industries/FieldDefinitions/CustomFieldDefinition";
import React, { FC, useContext, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { ThemeContext } from "styled-components";

const enum fields {
    label = "label",
    fontSizePx = "fontSizePx",
    iconSrc = "iconSrc",
    iconSizePx = "iconSizePx",
    linkDestination = "linkDestination",
    variant = "variant",
    alignment = "alignment",
    useButtonStyles = "useButtonStyles",
    buttonShape = "buttonShape",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.fontSizePx]: number;
        [fields.iconSrc]: React.ComponentType | string;
        [fields.iconSizePx]: number;
        [fields.linkDestination]: LinkFieldValue;
        [fields.variant]: "primary" | "secondary" | "tertiary";
        [fields.alignment]: "left" | "center" | "right";
        [fields.useButtonStyles]: boolean;
        [fields.buttonShape]: "rectangle" | "pill" | "rounded";
    };
}

interface MyTheme {
    colors: {
        primary: {
            main: string;
            contrast: string;
        };
        secondary: {
            main: string;
            contrast: string;
        };
        tertiary: {
            main: string;
            contrast: string;
        };
        text: {
            main: string;
        };
    };
}

export interface IconLinkStyles {
    containerClickable?: ClickablePresentationProps;
    wrapper?: InjectableCss;
    link?: ClickablePresentationProps;
    innerWrapper?: InjectableCss;
    iconWrapper?: InjectableCss;
    icon?: IconPresentationProps & IconThemableProps;
    labelWrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    button?: ButtonPresentationProps;
    buttonWrapper?: InjectableCss;
    buttonContainer?: InjectableCss;
}

// Styles for IconLink component
export const iconLinkStyles: IconLinkStyles = {
    buttonContainer: {
        css: css`
            height: 100%;
        `,
    },
    containerClickable: {
        css: css`
            height: 100%;
            width: 100%;
        `,
    },
    wrapper: {
        css: css`
            width: 100%;
            height: 94px;
            padding: 20px;
            border-radius: 25px;
            display: flex;
            align-items: center;
        `,
    },
    innerWrapper: {
        css: css`
            display: flex;
            align-content: center;
            justify-content: center;
            width: 100%;
        `,
    },
    iconWrapper: {
        css: css`
            width: 20%;
            display: flex;
            margin-right: 10px;
            justify-content: center;
            align-items: center;
        `,
    },
    icon: {
        css: css``,
    },
    labelWrapper: {
        css: css`
            width: 80%;
            display: flex;
        `,
    },
    label: {
        mergeCss: true,
        css: css`
            font-weight: 600;
            line-height: 27px;
        `,
    },
    button: {
        mergeCss: true,
        css: css`
            span {
                width: 100%;
            }
        `,
    },
};

// Styles for ButtonIconLink component
export const buttonIconLinkStyles: IconLinkStyles = {
    wrapper: {
        css: css`
            padding: 10px 10px;
            border-radius: 25px;
            display: inline-flex;
        `,
    },
    innerWrapper: {
        css: css`
            display: flex;
            align-content: center;
            margin: 10px 5px;
        `,
    },
    iconWrapper: {
        css: css`
            display: flex;
            margin-right: 5px;
            align-items: center;
        `,
    },
    icon: {},
    labelWrapper: {
        css: css`
            display: flex;

            align-items: ;
        `,
    },
    label: {
        mergeCss: true,
        css: css`
            margin-right: 5px;
        `,
    },
    button: {
        buttonType: "solid",
    },
    buttonWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            padding: 2px;
        `,
    },
};

// defines a component that accepts primary and secondary variants for IconLinkStyledWrapper
const IconLinkStyledWrapper = styled(StyledWrapper)<{
    variant: "primary" | "secondary" | "tertiary" | {};
    fontSize: number;
}>`
    ${props => {
        // takes component props and returns appropriate CSS rules based on variant prop value
        switch (props.variant) {
            case "primary":
                return css`
                    background-color: ${props.theme.colors.primary.main};
                    color: ${props.theme.colors.primary.contrast};
                    border-style: solid;
                    border-color: ${props.theme.colors.primary.main};
                `;
            case "secondary":
                return css`   
                    color: ${props.theme.colors.primary.main};
                    border-style: solid;
                   
                `;
            case "tertiary":
                return css`
                    color: ${props.theme.colors.secondary.main};
                    border-style: solid;

                    .icon-link-icon {
                        transition-duration: 0.2s;
                        transition-property: all;
                        transition-timing-function: ease-in-out;
                        transition-delay: 0s;
                    }

                    &:hover .icon-link-icon {
                        color: rgb(133, 21, 31);
                    }
                `;
        }
    }}
`;

// defines a component that allows for alignment options
const ButtonLinkContainer = styled(StyledWrapper)<{
    alignment: "left" | "center" | "right";
}>`
    display: flex;
    justify-content: ${props => {
        switch (props.alignment) {
            case "left":
                return "flex-start";
            case "center":
                return "center";
            case "right":
                return "flex-end";
            default:
                return "flex-start";
        }
    }};
`;

export const IconLink: FC<OwnProps> = ({ fields }) => {
    const { label, fontSizePx, iconSrc, iconSizePx, linkDestination, useButtonStyles } = fields;
    const { url } = useGetLink(linkDestination);
    const theme = useContext<MyTheme>(ThemeContext);

    const [iconKey, setIconKey] = useState(0);

    useEffect(() => {
        setIconKey(prevKey => prevKey + 1);
    }, [iconSrc]);

    const buttonStyles = fields.useButtonStyles ? buttonIconLinkStyles : iconLinkStyles;

    const CustomComponent = fields.useButtonStyles ? Button : Clickable;

    const customComponentProps = useButtonStyles ? buttonIconLinkStyles.wrapper : iconLinkStyles.link;

    const iconLinkVariant = useButtonStyles ? {} : fields.variant;

    const setButtonShape = useButtonStyles
        ? {
              shape: fields.buttonShape,
          }
        : {
              ...customComponentProps,
          };

    const containerStyle = useButtonStyles ? iconLinkStyles.buttonContainer : iconLinkStyles.containerClickable;

    return (
        <ButtonLinkContainer alignment={fields.alignment}>
            <Clickable href={url} {...containerStyle}>
                <IconLinkStyledWrapper
                    variant={iconLinkVariant}
                    fontSize={fontSizePx}
                    {...buttonStyles.wrapper}
                    data-test-selector="iconLink_wrapper"
                >
                    <CustomComponent
                        className="icon-link-button"
                        variant={fields.variant}
                        {...setButtonShape}
                        {...buttonStyles.buttonWrapper}
                    >
                        <StyledWrapper {...buttonStyles.innerWrapper}>
                            <StyledWrapper {...buttonStyles.iconWrapper}>
                                <Icon
                                    className="icon-link-icon"
                                    key={iconKey}
                                    {...buttonStyles.icon}
                                    color={
                                        fields.variant === "primary"
                                            ? theme.colors.primary.contrast
                                            : fields.variant === "secondary"
                                            ? theme.colors.primary.main
                                            : fields.variant === "tertiary"
                                            ? theme.colors.secondary.main
                                            : theme.colors.text.main
                                    }
                                    src={iconSrc}
                                    size={iconSizePx}
                                />
                            </StyledWrapper>
                            <StyledWrapper {...buttonStyles.labelWrapper}>
                                <Typography {...buttonStyles.label} size={fontSizePx}>
                                    {translate(label)}
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    </CustomComponent>
                </IconLinkStyledWrapper>
            </Clickable>
        </ButtonLinkContainer>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0
};

const iconTab = {
    displayName: "Icon",
    sortOrder: 1
};

const labelTab = {
    displayName: "Label",
    sortOrder: 2
};

export const definition: WidgetDefinition<CustomFieldDefinition> = {
    group: "Basic",
    icon: "rectangle-wide",
    fieldDefinitions: [        
        {
            name: fields.linkDestination,
            editorTemplate: "LinkField",
            defaultValue: { type: "Page", value: "" },
            fieldType: "General",
            isRequired: true,
            tab: basicTab,
            sortOrder: 10
        },
        {
            name: fields.variant,
            displayName: "Style Variant",
            fieldType: "General",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Primary", value: "primary" },
                { displayName: "Secondary", value: "secondary" },
                { displayName: "Tertiary", value: "tertiary" }
            ],
            defaultValue: "primary",
            isRequired: true,
            tab: basicTab,
            sortOrder: 20
        },
        {
            name: fields.useButtonStyles,
            displayName: "Make Link a Button",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
            tab: basicTab,
            sortOrder: 30
        },
        {
            name: fields.buttonShape,
            displayName: "Select Button Shape",
            editorTemplate: "DropDownField",
            defaultValue: "rectangle",
            fieldType: "General",
            options: [
                { displayName: "Rectangle", value: "rectangle" },
                { displayName: "Pill", value: "pill" },
                { displayName: "Rounded", value: "rounded" }
            ],
            hideEmptyOption: true,
            isRequired: true,
            isVisible: widget => widget.fields.useButtonStyles,
            tab: basicTab,
            sortOrder: 40
        },
        {
            name: fields.alignment,
            displayName: "Button Alignment",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Left", value: "left" },
                { displayName: "Center", value: "center" },
                { displayName: "Right", value: "right" },
            ],
            defaultValue: "left",
            fieldType: "General",
            isVisible: widget => widget.fields.useButtonStyles,
            tab: basicTab,
            sortOrder: 50
        },
        {
            name: fields.label,
            displayName: "Label",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            tab: labelTab,
            sortOrder: 100
        },
        {
            name: fields.fontSizePx,
            displayName: "Label Font Size (px)",
            editorTemplate: "IntegerField",
            defaultValue: 15,
            fieldType: "General",
            isRequired: true,
            min: 1,
            max: 48,
            tab: labelTab,
            sortOrder: 110
        },
        {
            name: fields.iconSizePx,
            displayName: "Icon Size (px)",
            editorTemplate: "IntegerField",
            defaultValue: 28,
            fieldType: "General",
            isRequired: true,
            min: 1,
            max: 48,
            tab: iconTab,
            sortOrder: 200
        },
        {
            name: fields.iconSrc,
            displayName: "Select Icon",
            editorTemplate: "IconPickerField",
            tooltip: "Select icon to appear in the widget",
            isRequired: false,
            defaultValue: "",
            fieldType: "General",
            tab: iconTab,
            sortOrder: 210
        }
    ]
};

const widgetModule: WidgetModule<CustomFieldDefinition> = {
    component: IconLink,
    definition
};

export default widgetModule;
